/**
 * What is standing: one label per claim, read off the graph's typed edges.
 *
 * A graph carries four relations between claims — the parent edge, the extra
 * parents a synthesis fuses (`bridgeFrom`), and the two a person draws
 * (`dependsOn`, `attacks`) — and until now a reader had to hold the second pair
 * in their head. Something refuted on one branch was a premise on another, and
 * the graph said nothing; a contradiction raised under one node about another
 * argued against it invisibly. This module answers one question for every node,
 * mechanically: is anything on record against this claim, and through which
 * edge?
 *
 * Five labels, first match wins, and each is a predicate over stored records
 * the model did not write about itself (AGENTS.md invariants 6, 8 and 10):
 *
 *   refuted     Its own live refuting source check or refuting recorded
 *               outcome — `liveChannelOf`, and nothing looser.
 *   undercut    A refuted claim in its FOUNDATION: the parent chain, the
 *               branches a synthesis fuses, and every claim it is drawn as
 *               depending on, followed transitively.
 *   contested   A claim still standing is drawn as attacking it.
 *   dead        `terminal` — the path was closed.
 *   standing    None of the above.
 *
 * COMPUTED LIKE TAINT, AND ON TOP OF IT. `taint.ts` already carries a
 * refutation down the parent chain and across `bridgeFrom`, with two rules that
 * keep it from overstating: a claim carrying a recorded result is exempt and
 * stops the descent, because an observation does not become doubtful when the
 * argument that suggested it fell over; and a claim reasoned AFTER the
 * refutation landed is exempt, because a later pass under a refuted node is
 * told of the refutation and what it writes is a re-derivation. `undercut`
 * reads `taintMap` for exactly that part of the foundation, so the label agrees
 * by construction with the strand the canvas detaches and the section the
 * report prints, and then extends the same walk across the drawn dependencies.
 * A DRAWN DEPENDENCY HAS NO TIME RULE: the edge is an assertion that one claim
 * rests on another, not a lineage a pass was told about, so a claim drawn today
 * as resting on a premise refuted yesterday is undercut whenever it was written
 * — and so is everything reasoned beneath it, because a pass under that claim
 * was never shown the premise on the other branch. The recorded-result boundary
 * holds on both walks, for the authority reason `channelOf` gives.
 *
 * AN ATTACK IS ONLY AS GOOD AS ITS ATTACKER. A refuted, undercut or closed
 * attacker CLEARS: its attack no longer counts, and a node it attacked reads as
 * standing with the fallen attacker named in `via`, so the UI can say "attacked,
 * and the attack was refuted" rather than silently dropping the edge. Liveness
 * is decided from the three labels that do not depend on attacks, and contested
 * is read over that — which is what makes a cycle (A attacks B, B attacks A)
 * terminate with both contested rather than recurse.
 *
 * EVERY LABEL STATES THE RULE THAT FIRED IT, naming the ids it read, on the
 * same contract `core/cues.ts` keeps for its WHY line: a label that cannot say
 * why it fired is a verdict handed down, which is the one thing this layer must
 * never be. Nothing is stored, nothing spends, and no number is invented.
 *
 * Memoised on the identity of `nodes`, which the store replaces on every
 * change, exactly as `taintMap` is and for the same reason: the renderer and
 * the inspector ask per frame.
 *
 * A leaf. It imports `./types`, `./taint` and `./rebase` and nothing else, so
 * the store, the codec and the UI can all read it without joining a cycle.
 */

import { liveChannelOf, outcomeLive, verificationLive } from './types'
import { CHANNEL_WORD } from './vocabulary'
import type { ReasonNode, Session } from './types'
import { taintSources } from './taint'
import { dependencyChildren } from './rebase'

export type StandingLabel = 'refuted' | 'undercut' | 'contested' | 'dead' | 'standing'

export interface Standing {
  label: StandingLabel
  /** The rule that fired, in a sentence a reader can re-derive — the cue contract. */
  rule: string
  /**
   * The ids the rule read: the refuted foundation, the live attackers, or the
   * attackers whose own fall cleared them. Empty where the rule read only the
   * node's own record.
   */
  via: string[]
}

/** The label headline. One map, so every surface names a label the same way. */
export const STANDING_LABEL: Record<StandingLabel, string> = {
  refuted: 'Refuted',
  undercut: 'Undercut',
  contested: 'Contested',
  dead: CHANNEL_WORD.dead,
  standing: 'Standing',
}

/** One line under the headline, saying what the label means and no more. */
export const STANDING_NOTE: Record<StandingLabel, string> = {
  refuted:
    'A recorded outcome, or a source check still current with its wording, refuted this claim itself.',
  undercut:
    'A claim this one rests on — up the parent chain or across a drawn dependency — has been refuted, and nothing here has been re-derived since.',
  contested: 'A claim still standing is drawn as attacking this one, and nothing has settled it.',
  dead: 'The path was closed: reasoned to a dead end, or closed by a constraint you set.',
  standing:
    'Nothing on record is against it: no refutation on it or under it, no attacker still standing, and the path is open.',
}

let cache: { nodes: Record<string, ReasonNode>; map: ReadonlyMap<string, Standing> } | null = null

/** Where one node stands, or null for an id that is not in the graph. */
export function standingOf(s: Session, nodeId: string): Standing | null {
  return standingMap(s).get(nodeId) ?? null
}

/**
 * Every node in `order`, mapped to where it stands. Complete, unlike `taintMap`:
 * a standing node carries a rule and possibly a cleared attacker, so absence
 * would lose both.
 */
export function standingMap(s: Session): ReadonlyMap<string, Standing> {
  if (cache && cache.nodes === s.nodes) return cache.map
  const map = compute(s)
  cache = { nodes: s.nodes, map }
  return map
}

/** How a cleared attacker fell, for the sentence that names it. */
type Fall = 'refuted' | 'undercut' | 'closed'

function compute(s: Session): ReadonlyMap<string, Standing> {
  const ids = s.order.filter((id) => !!s.nodes[id])
  const rank = new Map(ids.map((id, i) => [id, i] as const))
  const byRank = (a: string, b: string) => (rank.get(a) ?? 0) - (rank.get(b) ?? 0)

  /* ── refuted: the node's own record ─────────────────────────────────── */
  const refuted = new Set<string>()
  for (const id of ids) if (liveChannelOf(s.nodes[id]!) === 'refuted') refuted.add(id)

  /* ── undercut: taint, then the drawn dependencies ───────────────────── */
  // Every refuted claim in a node's foundation, by node. The parent-chain half
  // is taint's, read rather than recomputed so the two can never disagree.
  const foundation = new Map<string, Set<string>>()
  const found = (id: string, source: string) => {
    const set = foundation.get(id)
    if (set) set.add(source)
    else foundation.set(id, new Set([source]))
  }
  // EVERY source taint carried to a node, not only the nearest one `taintMap`
  // retains. A synthesis fusing two independently refuted branches is undercut
  // by both, and reading the single-source map named whichever was nearer and
  // dropped the other for good: the walk below starts at DRAWN edges, so it
  // cannot recover a second parent or `bridgeFrom` cause afterwards. The rules
  // stay in `taint.ts` — this reads the set that walk already computed.
  const taintSourcesByNode = taintSources(s)
  for (const [id, sources] of taintSourcesByNode) for (const source of sources) found(id, source)

  // Dependents by premise: the tree and bridge children `taint.ts` walks, and
  // beside them the claims drawn as resting on each node.
  const dependents = dependencyChildren(s)
  const drawn = new Map<string, string[]>()
  for (const id of ids) {
    for (const premise of s.nodes[id]!.dependsOn ?? []) {
      if (premise === id || !s.nodes[premise]) continue
      const list = drawn.get(premise)
      if (list) list.push(id)
      else drawn.set(premise, [id])
    }
  }

  // One walk per refutation, as taint does, so a node resting on two refuted
  // premises names both. The walk starts at the drawn dependents of the refuted
  // node and of everything taint already carried it to, and from there follows
  // every dependent edge with no time rule — the header says why — stopping at
  // a recorded result, which is exempt for the reason taint gives.
  for (const source of ids) {
    if (!refuted.has(source)) continue
    const seen = new Set<string>([source])
    const queue: string[] = []
    const startFrom = (premise: string) => {
      for (const dependent of drawn.get(premise) ?? []) queue.push(dependent)
    }
    startFrom(source)
    // Everything THIS refutation reached, by the same set and for the same
    // reason: keyed on the nearest source, a node tainted by a closer refutation
    // never handed this one its drawn dependents.
    for (const [id, sources] of taintSourcesByNode) if (sources.has(source)) startFrom(id)
    for (let at = 0; at < queue.length; at++) {
      const id = queue[at]!
      if (seen.has(id)) continue
      seen.add(id)
      const node = s.nodes[id]
      if (!node) continue
      // A recorded result is an authority boundary for every incoming path —
      // a LIVE one. `outcomeLive` here for the same reason `liveChannelOf` asks
      // it: a result recorded against wording the claim no longer carries is not
      // this claim's, and shielding on it stopped the walk at a node with
      // nothing standing behind it. The claim and everything under it then read
      // as standing while a refuted premise sat in their foundation.
      if (node.outcome && outcomeLive(node)) continue
      found(id, source)
      for (const child of dependents.get(id) ?? []) queue.push(child)
      for (const child of drawn.get(id) ?? []) queue.push(child)
    }
  }

  /* ── dead ───────────────────────────────────────────────────────────── */
  const closed = (id: string): boolean => s.nodes[id]!.terminal

  /** How an attacker fell, or null while it still stands. Read from the three
   *  labels that do not depend on attacks, which is what ends a cycle. */
  const fallOf = (id: string): Fall | null =>
    refuted.has(id) ? 'refuted' : foundation.has(id) ? 'undercut' : closed(id) ? 'closed' : null

  /* ── contested: the drawn attacks, by target ────────────────────────── */
  const attackers = new Map<string, string[]>()
  for (const id of ids) {
    for (const target of s.nodes[id]!.attacks ?? []) {
      if (target === id || !s.nodes[target]) continue
      const list = attackers.get(target)
      if (list) list.push(id)
      else attackers.set(target, [id])
    }
  }

  /* ── the labels, first match wins ───────────────────────────────────── */
  const out = new Map<string, Standing>()
  for (const id of ids) {
    const node = s.nodes[id]!
    const live: string[] = []
    const cleared: Array<{ id: string; fall: Fall }> = []
    for (const attacker of [...(attackers.get(id) ?? [])].sort(byRank)) {
      const fall = fallOf(attacker)
      if (fall) cleared.push({ id: attacker, fall })
      else live.push(attacker)
    }
    const fallen = cleared.map((c) => `${c.id} (${c.fall})`).join(', ')

    if (refuted.has(id)) {
      out.set(id, { label: 'refuted', rule: refutedRule(node), via: [] })
      continue
    }
    const premises = foundation.get(id)
    if (premises) {
      const via = [...premises].sort(byRank)
      out.set(id, { label: 'undercut', rule: undercutRule(id, via), via })
      continue
    }
    if (live.length) {
      out.set(id, {
        label: 'contested',
        rule:
          `FIRED ON · ${live.length} claim${live.length === 1 ? '' : 's'} still standing drawn as attacking ${id}: ${live.join(', ')}. ` +
          'An attacker counts only while it is itself neither refuted, undercut nor closed' +
          (cleared.length ? `; ${fallen} also attack it and no longer count.` : '.'),
        via: live,
      })
      continue
    }
    const attacked = cleared.length
      ? ` Attacked by ${fallen}, and a fallen attacker does not count.`
      : ''
    const via = cleared.map((c) => c.id)
    if (closed(id)) {
      out.set(id, {
        label: 'dead',
        rule:
          (node.terminalKind === 'constraint'
            ? `FIRED ON · ${id} closed only by a constraint you set — the argument itself was not exhausted — `
            : `FIRED ON · ${id} closed as reasoned out, `) +
          'with no refuting record on it or in its foundation and no claim still standing drawn as attacking it.' +
          attacked,
        via,
      })
      continue
    }
    out.set(id, {
      label: 'standing',
      rule:
        `FIRED ON · nothing on record against ${id}: no refuting record on it, none in its foundation, ` +
        'no claim still standing drawn as attacking it, and the path is open.' +
        attacked,
      via,
    })
  }
  return out
}

/**
 * The record that refuted the node itself, in the precedence `liveChannelOf`
 * reads it: an observation outranks a source, because a source only says. Both
 * are named when both hold, so the sentence is the whole of what was read.
 */
function refutedRule(node: ReasonNode): string {
  // The precedence this sentence names is `liveChannelOf`'s, so it has to read
  // the record the same way: a stale refuting outcome beside a live refuting
  // check would credit the observation for a refutation the check did alone.
  const byOutcome = node.outcome?.bearing === 'refuted' && outcomeLive(node)
  const byCheck = node.verification?.reading === 'refuted' && verificationLive(node)
  if (byOutcome) {
    return (
      `FIRED ON · an outcome recorded against ${node.id} with the bearing “refuted”` +
      (byCheck
        ? ', and a source check reading “refuted”, still current with its wording, agrees.'
        : '.')
    )
  }
  return `FIRED ON · a source check on ${node.id} reading “refuted”, still current with the claim’s wording. No outcome is recorded against it.`
}

function undercutRule(id: string, via: string[]): string {
  return (
    `FIRED ON · a refuted claim in the foundation of ${id}: ${via.join(', ')}. ` +
    `The foundation is the parent chain, the branches a synthesis fuses and every claim ${id} is drawn as depending on, followed transitively. ` +
    'A claim carrying a recorded result is exempt and stops the descent; along the parent chain a claim reasoned after the refutation landed is exempt, because the pass that wrote it was told of it; a drawn dependency exempts nothing, because it was asserted rather than reasoned.'
  )
}
